import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { CheckCircle2, XCircle, AlertCircle, Loader2, Sparkles, FileText, RefreshCw, BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import { quantAIExecute } from '../../lib/api';

/**
 * Dialog di esecuzione AI: mostra lo stato del two-pass (Generator → Validator → Save)
 * e il risultato finale con score, issues, strengths, paths salvati.
 *
 * Props:
 *  - open
 *  - onOpenChange
 *  - executeArgs: payload per /ai/execute (template, context, custom_text, generator_provider_id, validator_provider_id, min_score)
 *  - title: titolo del dialog (es. "🪄 Migliora con AI")
 *  - onSuccess: callback(result) chiamato UNA VOLTA quando l'execute completa con successo
 *    (anche se score basso — il caller decide cosa farne tramite result.saved_paths).
 *  - executeFn: opzionale, override del default quantAIExecute (es. per usare un
 *    endpoint diverso come /api/portfolios/:id/ai-analyze). Riceve executeArgs.
 */
const AIExecutionDialog = ({ open, onOpenChange, executeArgs, title = 'AI Execution', onSuccess, executeFn }) => {
  const [phase, setPhase] = useState('idle'); // idle | running | done | error
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const startRef = useRef(0);
  const tickerRef = useRef(null);
  const notifiedRef = useRef(false);

  useEffect(() => {
    if (!open) {
      setPhase('idle');
      setResult(null);
      setError(null);
      setElapsed(0);
      notifiedRef.current = false;
    }
  }, [open]);

  // Quando l'execute completa con successo, notifica il parent UNA SOLA VOLTA.
  // Il parent userà saved_paths per refetchare e auto-selezionare la nuova strategia.
  useEffect(() => {
    if (phase === 'done' && result && !notifiedRef.current) {
      notifiedRef.current = true;
      try { onSuccess?.(result); } catch (_) { /* no-op */ }
    }
  }, [phase, result, onSuccess]);

  const handleRun = async () => {
    if (!executeArgs?.generator_provider_id) {
      toast.error('Seleziona un provider AI prima');
      return;
    }
    setPhase('running');
    setResult(null);
    setError(null);
    startRef.current = Date.now();
    tickerRef.current = setInterval(() => {
      setElapsed(((Date.now() - startRef.current) / 1000));
    }, 200);
    try {
      const fn = executeFn || quantAIExecute;
      const r = await fn(executeArgs);
      setResult(r.data);
      setPhase('done');
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Errore execute');
      setPhase('error');
    } finally {
      if (tickerRef.current) clearInterval(tickerRef.current);
    }
  };

  // Auto-start quando si apre
  useEffect(() => {
    if (open && phase === 'idle' && executeArgs?.generator_provider_id) {
      handleRun();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, executeArgs?.generator_provider_id]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            {title}
          </DialogTitle>
          <DialogDescription>
            Template: <code className="font-mono">{executeArgs?.template}</code>
            {executeArgs?.context?.strategy && <> · strategia: <code className="font-mono">{executeArgs.context.strategy}</code></>}
            {executeArgs?.context?.asset && <> · asset: <code className="font-mono">{executeArgs.context.asset}</code></>}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-3 space-y-3">
          {phase === 'running' && (
            <div className="rounded-lg border bg-muted/30 p-4 flex items-start gap-3">
              <Loader2 className="w-5 h-5 animate-spin text-primary shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-sm">Esecuzione AI in corso…</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Step 1: Generator produce risposta · Step 2: Validator verifica qualità · Step 3: Salva nel vault
                </p>
                <p className="text-[10px] text-muted-foreground mt-2 font-mono">
                  ⏱ {elapsed.toFixed(1)}s · tipico 20-40s totali
                </p>
              </div>
            </div>
          )}

          {phase === 'error' && (
            <div className="rounded-lg border border-red-500/40 bg-red-50 dark:bg-red-950/30 p-4">
              <div className="flex items-start gap-2">
                <XCircle className="w-5 h-5 text-red-500 shrink-0" />
                <div>
                  <p className="font-medium text-sm text-red-700 dark:text-red-300">Esecuzione fallita</p>
                  <p className="text-xs text-muted-foreground mt-1">{error}</p>
                </div>
              </div>
            </div>
          )}

          {phase === 'done' && result && (
            <>
              {/* Status overview */}
              <div className="rounded-lg border bg-gradient-to-br from-primary/5 to-transparent p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Risultato</p>
                    <div className="flex items-center gap-2">
                      {result.validation.valid && result.validation.score >= 70 ? (
                        <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                      ) : result.validation.score >= 50 ? (
                        <AlertCircle className="w-6 h-6 text-amber-500" />
                      ) : (
                        <XCircle className="w-6 h-6 text-red-500" />
                      )}
                      <p className="text-2xl font-bold tabular-nums">
                        {result.validation.score}<span className="text-sm text-muted-foreground">/100</span>
                      </p>
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-1">
                      durata: {result.duration_seconds.toFixed(1)}s · gen: {result.generator.name} · val: {result.validator.same_as_generator ? 'self-check' : result.validator.name}
                    </p>
                  </div>
                  {result.saved_paths?.length > 0 && (
                    <div className="text-right">
                      <Badge className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 text-[10px]">
                        ✓ Salvato nel vault
                      </Badge>
                      <div className="mt-1 text-[10px] text-muted-foreground space-y-0.5">
                        {result.saved_paths.map(p => (
                          <p key={p} className="font-mono">{p}</p>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <p className="text-xs italic mt-2">"{result.validation.verdict}"</p>
              </div>

              {/* Issues + Strengths */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {result.validation.issues?.length > 0 && (
                  <div className="rounded-lg border border-amber-500/30 bg-amber-50/40 dark:bg-amber-950/20 p-3">
                    <p className="text-xs font-medium text-amber-700 dark:text-amber-300 flex items-center gap-1 mb-1.5">
                      <AlertCircle className="w-3 h-3" /> Issue ({result.validation.issues.length})
                    </p>
                    <ul className="text-[11px] space-y-1 list-disc pl-4 text-muted-foreground">
                      {result.validation.issues.map((iss, i) => <li key={i}>{iss}</li>)}
                    </ul>
                  </div>
                )}
                {result.validation.strengths?.length > 0 && (
                  <div className="rounded-lg border border-emerald-500/30 bg-emerald-50/40 dark:bg-emerald-950/20 p-3">
                    <p className="text-xs font-medium text-emerald-700 dark:text-emerald-300 flex items-center gap-1 mb-1.5">
                      <CheckCircle2 className="w-3 h-3" /> Punti di forza
                    </p>
                    <ul className="text-[11px] space-y-1 list-disc pl-4 text-muted-foreground">
                      {result.validation.strengths.map((s, i) => <li key={i}>{s}</li>)}
                    </ul>
                  </div>
                )}
              </div>

              {result.save_skipped_reason && (
                <div className="rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-500/30 p-2 text-xs">
                  ⚠ Output non salvato nel vault: {result.save_skipped_reason}
                </div>
              )}

              {/* Output preview (collapsed) */}
              <details className="rounded-lg border">
                <summary className="px-3 py-2 cursor-pointer text-xs font-medium flex items-center gap-2">
                  <FileText className="w-3.5 h-3.5" /> Mostra output completo
                  <span className="text-muted-foreground font-normal">({result.generation?.length || 0} char)</span>
                </summary>
                <pre className="text-[10px] font-mono p-3 bg-muted/30 max-h-96 overflow-auto whitespace-pre-wrap leading-relaxed">
                  {result.generation}
                </pre>
              </details>
            </>
          )}
        </div>

        <DialogFooter className="mt-3 gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {phase === 'done' ? 'Chiudi' : 'Annulla'}
          </Button>
          {phase === 'done' && (
            <Button variant="outline" onClick={handleRun}>
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Rilancia
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AIExecutionDialog;
